// ProfilePage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  getUserInfo,
  getReviews,
  deleteReview,
  getUserOrders,
  updateReview,
} from '../api/apiendpoint';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaUser, FaShoppingCart, FaStar } from 'react-icons/fa';
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Alert,
  Dropdown,
  Modal,
  Form,
  Badge,
  ListGroup,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import { useAuth } from '../context/AuthContext';

// Constants
const PAYMENT_FILTER_ALL = 'all';
const RATING_MIN = 1;
const RATING_MAX = 5;

// Custom hook for toast notifications
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, variant = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
};

// Loading component
const LoadingSpinner = ({ message = 'Loading...' }) => (
  <div className="text-center mt-5" role="status" aria-live="polite">
    <Spinner animation="border" role="status" aria-hidden="true" />
    <p className="mt-2">{message}</p>
  </div>
);

// Error boundary component
const ErrorAlert = ({ error, onRetry }) => (
  <Alert variant="danger" className="mt-3">
    <Alert.Heading>Something went wrong!</Alert.Heading>
    <p>{error}</p>
    {onRetry && (
      <Button variant="outline-danger" onClick={onRetry}>
        Try Again
      </Button>
    )}
  </Alert>
);

// Profile info card component
const ProfileInfoCard = ({ userInfo }) => (
  <Card className="mb-4 shadow-sm">
    <Card.Header style={{ backgroundColor: '#7dbfa2', color: 'white' }}>
      <FaUser className="me-2" />
      Profile Information
    </Card.Header>
    <Card.Body>
      {userInfo ? (
        <div className="profile-info">
          <div className="mb-2">
            <strong>Username:</strong> {userInfo.username || 'N/A'}
          </div>
          <div className="mb-2">
            <strong>Email:</strong> {userInfo.email || 'N/A'}
          </div>
          <div className="mb-2">
            <strong>Full Name:</strong> {`${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || 'N/A'}
          </div>
          <div className="mb-2">
            <strong>City:</strong> {userInfo.city || 'N/A'}
          </div>
          <div className="mb-2">
            <strong>Address:</strong> {userInfo.address || 'N/A'}
          </div>
          <div className="mb-2">
            <strong>Phone:</strong> {userInfo.phone_number || 'N/A'}
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading profile...</span>
        </div>
      )}
    </Card.Body>
  </Card>
);

// Order summary card component
const OrderSummaryCard = ({ orders, paymentFilter, onFilterChange }) => {
  const filteredOrders = useMemo(() => {
    return paymentFilter === PAYMENT_FILTER_ALL
      ? orders
      : orders.filter((order) => order.payment_method === paymentFilter);
  }, [orders, paymentFilter]);

  const totalSpent = useMemo(() => {
    return filteredOrders.reduce((sum, order) => sum + parseFloat(order.subtotal || 0), 0);
  }, [filteredOrders]);

  const uniqueMethods = useMemo(() => {
    return [...new Set(orders.map((order) => order.payment_method).filter(Boolean))];
  }, [orders]);

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header style={{ backgroundColor: 'black', color: 'white' }}>
        <FaShoppingCart className="me-2" />
        Order Summary
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <div className="mb-2">
            <strong>Total Orders:</strong> {filteredOrders.length}
          </div>
          <div className="mb-3">
            <strong>Total Spent:</strong> ${totalSpent.toFixed(2)}
          </div>
          
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" size="sm">
              Payment Method: {paymentFilter === PAYMENT_FILTER_ALL ? 'All' : paymentFilter}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item 
                onClick={() => onFilterChange(PAYMENT_FILTER_ALL)}
                active={paymentFilter === PAYMENT_FILTER_ALL}
              >
                All Methods
              </Dropdown.Item>
              {uniqueMethods.map((method) => (
                <Dropdown.Item 
                  key={method} 
                  onClick={() => onFilterChange(method)}
                  active={paymentFilter === method}
                >
                  {method}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {filteredOrders.length > 0 ? (
          <div className="order-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <ListGroup variant="flush">
              {filteredOrders.map((order) => (
                <ListGroup.Item key={order.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>#{order.id}</strong>
                    <Badge bg="secondary" className="ms-2">
                      {order.payment_method}
                    </Badge>
                  </div>
                  <span className="text-success fw-bold">
                    ${parseFloat(order.subtotal || 0).toFixed(2)}
                  </span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        ) : (
          <Alert variant="info" className="mt-3">
            No orders found for the selected payment method.
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

// Review item component
const ReviewItem = ({ review, onEdit, onDelete }) => (
  <ListGroup.Item className="d-flex justify-content-between align-items-start">
    <div className="flex-grow-1">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{review.title || review.name || 'Untitled Review'}</h6>
        <Badge bg="warning" text="dark" className="ms-2">
          <FaStar className="me-1" />
          {review.rating}/{RATING_MAX}
        </Badge>
      </div>
      <p className="mb-1 text-muted">{review.content || 'No content provided'}</p>
      <small className="text-muted">
        {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'Unknown date'}
      </small>
    </div>
    <div className="ms-3">
      <Button
        variant="outline-primary"
        size="sm"
        className="me-2"
        onClick={() => onEdit(review)}
        aria-label={`Edit review: ${review.title || review.name}`}
      >
        <FaEdit />
      </Button>
      <Button
        variant="outline-danger"
        size="sm"
        onClick={() => onDelete(review.id)}
        aria-label={`Delete review: ${review.title || review.name}`}
      >
        <FaTrash />
      </Button>
    </div>
  </ListGroup.Item>
);

// Reviews section component
const ReviewsSection = ({ reviews, onEdit, onDelete }) => (
  <Card className="shadow-sm">
    <Card.Header style={{ backgroundColor: '#7dbfa2', color: 'white' }}>
      <FaStar className="me-2" />
      My Reviews ({reviews.length})
    </Card.Header>
    <Card.Body>
      {reviews.length === 0 ? (
        <Alert variant="info">
          You haven't written any reviews yet. Start reviewing products to help other customers!
        </Alert>
      ) : (
        <ListGroup variant="flush">
          {reviews.map((review) => (
            <ReviewItem
              key={review.id}
              review={review}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </ListGroup>
      )}
    </Card.Body>
  </Card>
);

// Edit review modal component
const EditReviewModal = ({ 
  show, 
  onHide, 
  review, 
  onReviewChange, 
  onSubmit, 
  loading 
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onReviewChange({ ...review, [name]: value });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Review</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="reviewRating">
            <Form.Label>Rating *</Form.Label>
            <Form.Control
              type="number"
              name="rating"
              min={RATING_MIN}
              max={RATING_MAX}
              value={review?.rating || ''}
              onChange={handleInputChange}
              required
              aria-describedby="ratingHelp"
            />
            <Form.Text id="ratingHelp" className="text-muted">
              Rate from {RATING_MIN} to {RATING_MAX} stars
            </Form.Text>
          </Form.Group>
          
          <Form.Group className="mb-3" controlId="reviewContent">
            <Form.Label>Comment *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="content"
              value={review?.content || ''}
              onChange={handleInputChange}
              required
              maxLength={1000}
              aria-describedby="contentHelp"
            />
            <Form.Text id="contentHelp" className="text-muted">
              {review?.content?.length || 0}/1000 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Main ProfilePage component
const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();

  // State management
  const [state, setState] = useState({
    userInfo: null,
    reviews: [],
    orders: [],
    loading: true,
    error: null,
    paymentFilter: PAYMENT_FILTER_ALL,
  });

  // Modal states
  const [modalState, setModalState] = useState({
    showEditModal: false,
    reviewToEdit: null,
    editLoading: false,
  });

  // Load initial data
  const loadData = useCallback(async () => {
    if (!user?.username) return;

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const [userDetails, userReviews, userOrders] = await Promise.all([
        getUserInfo(),
        getReviews(user.username),
        getUserOrders(),
      ]);

      setState(prev => ({
        ...prev,
        userInfo: userDetails,
        reviews: userReviews || [],
        orders: userOrders || [],
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to load profile data:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load profile data. Please try again.',
      }));
      addToast('Failed to load profile data', 'danger');
    }
  }, [user?.username, addToast]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle review deletion
  const handleDeleteReview = useCallback(async (reviewId) => {
    const confirmed = window.confirm('Are you sure you want to delete this review? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteReview(reviewId);
      setState(prev => ({
        ...prev,
        reviews: prev.reviews.filter(review => review.id !== reviewId),
      }));
      addToast('Review deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete review:', error);
      addToast('Failed to delete review', 'danger');
    }
  }, [addToast]);

  // Handle edit modal
  const handleEditClick = useCallback((review) => {
    setModalState({
      showEditModal: true,
      reviewToEdit: { ...review },
      editLoading: false,
    });
  }, []);

  const handleEditSubmit = useCallback(async (e) => {
    e.preventDefault();
    setModalState(prev => ({ ...prev, editLoading: true }));

    try {
      await updateReview(modalState.reviewToEdit.id, modalState.reviewToEdit);
      
      setState(prev => ({
        ...prev,
        reviews: prev.reviews.map(review =>
          review.id === modalState.reviewToEdit.id ? modalState.reviewToEdit : review
        ),
      }));

      setModalState({ showEditModal: false, reviewToEdit: null, editLoading: false });
      addToast('Review updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update review:', error);
      addToast('Failed to update review', 'danger');
      setModalState(prev => ({ ...prev, editLoading: false }));
    }
  }, [modalState.reviewToEdit, addToast]);

  const handleModalClose = useCallback(() => {
    setModalState({ showEditModal: false, reviewToEdit: null, editLoading: false });
  }, []);

  const handleReviewChange = useCallback((updatedReview) => {
    setModalState(prev => ({ ...prev, reviewToEdit: updatedReview }));
  }, []);

  const handlePaymentFilterChange = useCallback((filter) => {
    setState(prev => ({ ...prev, paymentFilter: filter }));
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Show loading state
  if (state.loading) {
    return <LoadingSpinner message="Loading your profile..." />;
  }

  // Show error state
  if (state.error) {
    return (
      <Container className="my-5">
        <ErrorAlert error={state.error} onRetry={loadData} />
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Row>
        {/* Left Column - Profile Info and Order Summary */}
        <Col lg={4} md={5}>
          <ProfileInfoCard userInfo={state.userInfo} />
          <OrderSummaryCard
            orders={state.orders}
            paymentFilter={state.paymentFilter}
            onFilterChange={handlePaymentFilterChange}
          />
        </Col>

        {/* Right Column - Reviews */}
        <Col lg={8} md={7}>
          <ReviewsSection
            reviews={state.reviews}
            onEdit={handleEditClick}
            onDelete={handleDeleteReview}
          />
        </Col>
      </Row>

      {/* Edit Review Modal */}
      <EditReviewModal
        show={modalState.showEditModal}
        onHide={handleModalClose}
        review={modalState.reviewToEdit}
        onReviewChange={handleReviewChange}
        onSubmit={handleEditSubmit}
        loading={modalState.editLoading}
      />

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            onClose={() => removeToast(toast.id)}
            show={true}
            delay={5000}
            autohide
            bg={toast.variant}
          >
            <Toast.Header>
              <strong className="me-auto">Notification</strong>
            </Toast.Header>
            <Toast.Body className={toast.variant === 'danger' ? 'text-white' : ''}>
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </Container>
  );
};

export default ProfilePage;